import { createMCPClient } from "@ai-sdk/mcp";
import { getApiClient } from "@src/api";
import { getAccessToken, getShareKey } from "@src/auth/auth-utils";
import { useGetSupportedAgents } from "@src/components/cards/prompt-card/queries/get-supported-agents";
import { planQueryKeys } from "@src/components/plan/queries/query-keys";
import config from "@src/config";
import { DEFAULT_LLM_MODEL } from "@src/config/constants";
import { useQueryClient } from "@tanstack/react-query";
import { type ModelMessage, jsonSchema, type streamText, tool } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	assistantPlainText,
	chatNameFromMessage,
	maybeRenameChatWithGeneratedTitle,
	resolveCachedChatName,
} from "../agent-chat-auto-title";
import {
	toAgentChatMessage,
	toSaveChatMessageInput,
} from "../agent-chat-persistence-utils";
import {
	clearAgentSession,
	getAgentSession,
	setAgentSession,
} from "../agent-session";
import { buildAgentSystemPrompt } from "../agent-system-prompt";
import { createBoardTools, createChatTools } from "../agent-tools";
import { createAppTools } from "../app-tools";
import {
	AGENT_CHAT_MAX_TOOL_STEPS,
	filterBoardToolsForEditable,
} from "../board-agent-config";
import {
	AGENT_CHAT_MAX_MESSAGE_LENGTH,
	AGENT_CHAT_MIN_SEND_INTERVAL_MS,
} from "../constants";
import { chatAggregateId } from "../document-refs";
import { listImageGenerationModels } from "../image-generation-models";
import { createLibraryTools } from "../library-tools";
import { createLiveBoardActionsProxy } from "../live-board-actions-proxy";
import {
	useCreateChat,
	useRenameChat,
	useSaveChatMessages,
} from "../mutations/chat-mutations";
import {
	formatChoiceSelectionMessage,
	markChoiceResponded,
	validateChoiceSelection,
} from "../present-options";
import { getChatMessagesQuery } from "../queries/chat-queries";
import { resolvePeopleMentionContext } from "../resolve-people-mention-context";
import { resolveReferenceContext } from "../resolve-reference-context";
import {
	continueAgentChatAfterApprovals,
	runAgentChatTurn,
} from "../run-agent-chat-turn";
import {
	cancelPendingApprovals,
	collectApprovalResponses,
	completeDeniedApprovalToolCalls,
	getPendingApprovals,
	hasPendingApprovals,
	markApprovalRespondedIncludingDuplicates,
	markApprovalsResponded,
} from "../tool-approvals";
import {
	collectToolCapableChatModels,
	readStoredChatLlmModel,
	resolveChatLlmModel,
	writeStoredChatLlmModel,
} from "../tool-capable-chat-models";
import type {
	AgentChatAttachment,
	AgentChatMessage,
	AgentChatPersonMention,
	AgentChatReference,
	AgentChatStatus,
} from "../types";
import { useAppAgentContext } from "./app-agent-context";
import { useBoardAgentContext } from "./board-agent-context";
import { useLibraryAgentContext } from "./library-agent-context";

export { DEFAULT_LLM_MODEL, AGENT_CHAT_MAX_TOOL_STEPS };

type AgentChatTools = NonNullable<Parameters<typeof streamText>[0]["tools"]>;

const EMPTY_EXTERNAL_TOOLS: AgentChatTools = {};

type McpClient = Awaited<ReturnType<typeof createMCPClient>>;

export function useAgentChat() {
	const boardAgent = useBoardAgentContext();
	const libraryAgent = useLibraryAgentContext();
	const appAgent = useAppAgentContext();
	const {
		data: supportedAgentsData,
		isPending: areSupportedAgentsPending,
		isFetching: areSupportedAgentsFetching,
		isError: areSupportedAgentsError,
		refetch: refetchSupportedAgents,
	} = useGetSupportedAgents();
	const initialSession = getAgentSession();
	const [activeChatId, setActiveChatId] = useState<string | null>(
		() => initialSession.chatId,
	);
	const [isLoadingChat, setIsLoadingChat] = useState(false);
	const [messages, setMessages] = useState<AgentChatMessage[]>(
		() => initialSession.messages,
	);
	const [status, setStatus] = useState<AgentChatStatus>(
		() => initialSession.status,
	);
	const [error, setError] = useState<string | null>(() => initialSession.error);
	const [llmModel, setLlmModelState] = useState(
		() =>
			initialSession.llmModel ?? readStoredChatLlmModel() ?? config.llmModel,
	);
	const [osTools, setOsTools] = useState<AgentChatTools>(EMPTY_EXTERNAL_TOOLS);
	const [mcpTools, setMcpTools] =
		useState<AgentChatTools>(EMPTY_EXTERNAL_TOOLS);
	const mcpToolsRef = useRef<AgentChatTools>(EMPTY_EXTERNAL_TOOLS);
	const abortControllerRef = useRef<AbortController | null>(null);
	const mcpClientRef = useRef<McpClient | null>(null);
	const mcpLoadPromiseRef = useRef<Promise<void> | null>(null);
	const mcpToolsLoadedRef = useRef(false);
	const isMountedRef = useRef(true);
	const messagesRef = useRef(messages);
	const continuationMessagesRef = useRef<ModelMessage[] | null>(
		initialSession.continuationMessages,
	);
	const isSubmittingRef = useRef(false);
	const lastSendAtRef = useRef(0);
	const chatLoadRequestIdRef = useRef(0);
	const activeChatIdRef = useRef(activeChatId);
	activeChatIdRef.current = activeChatId;
	const seedNameByChatIdRef = useRef(new Map<string, string>());
	const autoTitleAttemptedRef = useRef(new Set<string>());

	messagesRef.current = messages;

	const queryClient = useQueryClient();
	const { mutateAsync: createChat } = useCreateChat();
	const { mutateAsync: renameChat } = useRenameChat();
	const { mutateAsync: saveChatMessages } = useSaveChatMessages();

	const tryAutoTitleChat = useCallback(
		(chatId: string, turnMessages: AgentChatMessage[], modelId: string) => {
			const seedName = seedNameByChatIdRef.current.get(chatId);
			if (!seedName || autoTitleAttemptedRef.current.has(chatId)) {
				return;
			}
			if (!assistantPlainText(turnMessages)) {
				return;
			}
			autoTitleAttemptedRef.current.add(chatId);
			void maybeRenameChatWithGeneratedTitle({
				chatId,
				messages: turnMessages,
				llmModel: modelId,
				seedName,
				alreadyAttempted: false,
				getCurrentName: () => resolveCachedChatName(queryClient, chatId),
				renameChat,
			}).catch((titleErr) => {
				console.error("[useAgentChat] Failed to auto-title chat:", titleErr);
			});
		},
		[queryClient, renameChat],
	);

	useEffect(() => {
		setAgentSession({
			chatId: activeChatId,
			messages,
			status,
			error,
			continuationMessages: continuationMessagesRef.current,
			llmModel,
		});
	}, [activeChatId, messages, status, error, llmModel]);

	const toolModels = useMemo(
		() => collectToolCapableChatModels(supportedAgentsData?.allAgents),
		[supportedAgentsData?.allAgents],
	);
	const resolvedLlmModel = useMemo(() => {
		if (toolModels.length === 0) {
			return llmModel;
		}

		return resolveChatLlmModel(toolModels, llmModel, config.llmModel);
	}, [llmModel, toolModels]);
	// Treat refetch-with-no-models as pending so Retry shows loading, not a
	// stuck failure/empty state.
	const areToolModelsPending =
		toolModels.length === 0 &&
		(areSupportedAgentsPending || areSupportedAgentsFetching);
	const toolModelsLoadFailed = !areToolModelsPending && areSupportedAgentsError;
	const areToolModelsReady =
		!areToolModelsPending &&
		!areSupportedAgentsError &&
		toolModels.length > 0 &&
		toolModels.some((model) => model.modelId === resolvedLlmModel);

	const retryToolModelsLoad = useCallback(() => {
		void refetchSupportedAgents();
	}, [refetchSupportedAgents]);

	useEffect(() => {
		if (!areToolModelsReady || resolvedLlmModel === llmModel) {
			return;
		}

		setLlmModelState(resolvedLlmModel);
		writeStoredChatLlmModel(resolvedLlmModel);
	}, [areToolModelsReady, llmModel, resolvedLlmModel]);

	const setLlmModel = useCallback(
		(modelId: string) => {
			if (
				toolModels.length > 0 &&
				!toolModels.some((model) => model.modelId === modelId)
			) {
				return;
			}

			setLlmModelState(modelId);
			writeStoredChatLlmModel(modelId);
			setAgentSession({ llmModel: modelId });
		},
		[toolModels],
	);

	const ensureMcpToolsLoaded = useCallback((): Promise<void> => {
		const mcpUrl = config.mcpHttpUrl;
		if (!mcpUrl || mcpToolsLoadedRef.current) {
			return Promise.resolve();
		}
		if (mcpLoadPromiseRef.current) {
			return mcpLoadPromiseRef.current;
		}

		const promise = (async () => {
			let mcpClient: McpClient | null = null;
			try {
				const token = await getAccessToken();
				if (!isMountedRef.current) {
					return;
				}

				const headers: Record<string, string> = {};
				if (token) {
					headers.Authorization = `Bearer ${token}`;
				}

				mcpClient = await createMCPClient({
					transport: {
						type: "http",
						url: mcpUrl,
						headers,
						fetch: (url, options) => window.fetch(url, options),
					},
				});

				if (!isMountedRef.current) {
					await mcpClient.close();
					return;
				}

				mcpClientRef.current = mcpClient;
				const tools = (await mcpClient.tools()) as AgentChatTools;

				if (!isMountedRef.current) {
					mcpClientRef.current = null;
					await mcpClient.close();
					return;
				}

				mcpToolsRef.current = tools;
				setMcpTools(tools);
				mcpToolsLoadedRef.current = true;
			} catch (err) {
				if (mcpClient && mcpClientRef.current === mcpClient) {
					mcpClientRef.current = null;
				}
				if (mcpClient) {
					await mcpClient.close().catch(() => undefined);
				}
				if (isMountedRef.current) {
					console.error("[useAgentChat] Failed to load MCP tools:", err);
				}
			} finally {
				mcpLoadPromiseRef.current = null;
			}
		})();

		mcpLoadPromiseRef.current = promise;
		return promise;
	}, []);

	useEffect(() => {
		const loadOsTools = async () => {
			const electronAPI = window.electronAPI;
			if (!electronAPI || !electronAPI.isElectron) {
				return;
			}

			try {
				const settings = await electronAPI.getCliSettings();
				if (!settings?.hasConsentedToCli) {
					return;
				}

				const response = await electronAPI.listOsTools();
				if (!response || !Array.isArray(response.tools)) {
					return;
				}

				const toolsRecord: AgentChatTools = {};
				for (const t of response.tools) {
					if (!t.name.startsWith("os_")) {
						continue;
					}

					toolsRecord[t.name] = tool({
						description: t.description,
						inputSchema: jsonSchema(t.inputSchema),
						execute: async (args) => {
							return await electronAPI.executeOsTool(
								t.name,
								args as Record<string, unknown>,
							);
						},
					});
				}
				setOsTools(toolsRecord);
			} catch (err) {
				console.error(
					"[useAgentChat] Failed to load OS tools from Electron:",
					err,
				);
			}
		};

		void loadOsTools();
	}, []);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
			abortControllerRef.current?.abort();
			const mcpClient = mcpClientRef.current;
			mcpClientRef.current = null;
			mcpLoadPromiseRef.current = null;
			mcpToolsLoadedRef.current = false;
			mcpToolsRef.current = EMPTY_EXTERNAL_TOOLS;
			if (mcpClient) {
				void mcpClient.close();
			}
		};
	}, []);

	const statusRef = useRef(status);
	statusRef.current = status;
	const saveChatMessagesRef = useRef(saveChatMessages);
	saveChatMessagesRef.current = saveChatMessages;
	const resolvedLlmModelRef = useRef(resolvedLlmModel);
	resolvedLlmModelRef.current = resolvedLlmModel;

	const abortStream = useCallback(() => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
		isSubmittingRef.current = false;

		if (statusRef.current === "awaiting_approval") {
			const previousMessages = messagesRef.current;
			const cancelled = cancelPendingApprovals(previousMessages);
			messagesRef.current = cancelled;
			setMessages(cancelled);
			continuationMessagesRef.current = null;
			setStatus("idle");

			const currentChatId = activeChatIdRef.current;
			if (currentChatId) {
				const changedMessages = cancelled.filter(
					(msg, idx) => msg !== previousMessages[idx],
				);
				if (changedMessages.length > 0) {
					void saveChatMessagesRef
						.current({
							chatId: currentChatId,
							messages: changedMessages.map((msg) =>
								toSaveChatMessageInput(
									currentChatId,
									msg,
									resolvedLlmModelRef.current,
								),
							),
						})
						.catch((saveErr) => {
							console.error(
								"[useAgentChat] Failed to save cancelled approval messages:",
								saveErr,
							);
						});
				}
			}
			return;
		}

		setStatus((current) =>
			current === "submitting" || current === "awaiting_choice"
				? "idle"
				: current,
		);
	}, []);

	const startNewChat = useCallback(() => {
		chatLoadRequestIdRef.current += 1;
		abortStream();
		setIsLoadingChat(false);
		setMessages([]);
		messagesRef.current = [];
		setActiveChatId(null);
		activeChatIdRef.current = null;
		setStatus("idle");
		setError(null);
		continuationMessagesRef.current = null;
		clearAgentSession();
	}, [abortStream]);

	const loadChat = useCallback(
		async (chatId: string) => {
			const requestId = ++chatLoadRequestIdRef.current;
			abortStream();
			setIsLoadingChat(true);
			setError(null);
			setActiveChatId(chatId);
			activeChatIdRef.current = chatId;
			try {
				const client = getApiClient();
				const normalizedChatId = chatAggregateId(chatId);
				const shareKey = getShareKey();
				const result = await client.query({
					query: getChatMessagesQuery,
					variables: {
						chatId: normalizedChatId,
						...(shareKey ? { shareKey } : {}),
					},
					fetchPolicy: "network-only",
				});
				if (chatLoadRequestIdRef.current !== requestId) {
					return;
				}
				const rawNodes = result.data?.chats?.nodes?.[0]?.messages?.nodes ?? [];
				const loadedMessages = rawNodes.map(toAgentChatMessage);

				setMessages(loadedMessages);
				messagesRef.current = loadedMessages;
				setStatus("idle");
				continuationMessagesRef.current = null;
				setAgentSession({
					chatId,
					messages: loadedMessages,
					status: "idle",
					error: null,
					continuationMessages: null,
				});
			} catch (err) {
				if (chatLoadRequestIdRef.current !== requestId) {
					return;
				}
				const msg =
					err instanceof Error ? err.message : "Failed to load chat messages";
				setError(msg);
				setStatus("error");
			} finally {
				if (chatLoadRequestIdRef.current === requestId) {
					setIsLoadingChat(false);
				}
			}
		},
		[abortStream],
	);

	const dismissError = useCallback(() => {
		setError(null);
		setStatus((current) => (current === "error" ? "idle" : current));
	}, []);

	const imageModelsRef = useRef(
		listImageGenerationModels(undefined, undefined),
	);
	imageModelsRef.current = listImageGenerationModels(
		supportedAgentsData?.allAgents,
		supportedAgentsData?.myAgents,
	);

	const selectionGateRef = useRef({ presentOptionsCalled: false });
	const hasBoardTools = Boolean(boardAgent?.actions);
	const skillAvailability = useMemo(
		() => ({
			hasBoardTools,
			hasLibraryTools: Boolean(libraryAgent?.actions),
			hasAppTools: Boolean(appAgent?.actions),
		}),
		[appAgent?.actions, hasBoardTools, libraryAgent?.actions],
	);

	const chatTools = useMemo(
		() =>
			createChatTools({
				getImageModels: () => imageModelsRef.current,
				getMessages: () => messagesRef.current,
				selectionGate: selectionGateRef.current,
				skillAvailability,
			}),
		[skillAvailability],
	);

	const liveBoardActions = useMemo(() => createLiveBoardActionsProxy(), []);

	const boardTools = useMemo(() => {
		if (!boardAgent?.actions) {
			return undefined;
		}

		const tools = createBoardTools({
			actions: liveBoardActions,
			getMessages: () => messagesRef.current,
		});

		return filterBoardToolsForEditable(tools, {
			editable: boardAgent.snapshot?.editable,
		});
	}, [boardAgent?.actions, boardAgent?.snapshot?.editable, liveBoardActions]);

	const libraryTools = useMemo(() => {
		if (!libraryAgent?.actions) {
			return undefined;
		}

		return createLibraryTools({
			actions: libraryAgent.actions,
		});
	}, [libraryAgent?.actions]);

	const appTools = useMemo(() => {
		if (!appAgent?.actions) {
			return undefined;
		}

		return createAppTools({
			actions: appAgent.actions,
		});
	}, [appAgent?.actions]);

	const agentTools = useMemo((): AgentChatTools => {
		const baseTools: AgentChatTools = {
			...chatTools,
			...(boardTools ?? {}),
			...(libraryTools ?? {}),
			...(appTools ?? {}),
			...osTools,
			...mcpTools,
		};

		return baseTools;
	}, [appTools, boardTools, chatTools, libraryTools, mcpTools, osTools]);

	const systemPrompt = useMemo(
		() => buildAgentSystemPrompt(skillAvailability),
		[skillAvailability],
	);

	const applyTurnResult = useCallback(
		(result: Awaited<ReturnType<typeof runAgentChatTurn>>) => {
			if (result.aborted) {
				continuationMessagesRef.current = null;
				setStatus("idle");
				return;
			}

			if (!result.ok) {
				continuationMessagesRef.current = null;
				setError(result.error);
				setStatus("error");
				return;
			}

			messagesRef.current = result.messages;
			setMessages(result.messages);
			continuationMessagesRef.current = result.awaitingApproval
				? (result.continuationMessages ?? null)
				: null;

			if (result.awaitingApproval) {
				setStatus("awaiting_approval");
			} else if (result.awaitingChoice) {
				setStatus("awaiting_choice");
			} else {
				setStatus("idle");
			}

			if (result.touchedBoard) {
				boardAgent?.focusSessionTouchedShapes?.(result.touchedShapeIds);
			}

			// Refresh CreditsBudget so Profile / gates update without reload.
			void queryClient.invalidateQueries({
				queryKey: planQueryKeys.myPlan(),
			});
		},
		[boardAgent, queryClient],
	);

	const sendMessage = useCallback(
		async (
			text: string,
			options?: {
				baseMessages?: AgentChatMessage[];
				attachments?: AgentChatAttachment[];
				onChatCreated?: (chatId: string) => void;
				mentions?: AgentChatPersonMention[];
				references?: AgentChatReference[];
			},
		): Promise<boolean> => {
			const trimmed = text.trim();
			const attachments = options?.attachments;
			const hasAttachments = (attachments?.length ?? 0) > 0;

			if ((!trimmed && !hasAttachments) || isSubmittingRef.current) {
				return false;
			}

			if (trimmed.toLowerCase() === "/clear") {
				startNewChat();
				return true;
			}

			if (!areToolModelsReady) {
				return false;
			}

			// Suggestion chips / send can fire without a textarea focus event.
			await ensureMcpToolsLoaded();

			const activeTools: AgentChatTools = {
				...agentTools,
				...mcpToolsRef.current,
			};

			if (trimmed.length > AGENT_CHAT_MAX_MESSAGE_LENGTH) {
				setError(
					`Message is too long (max ${AGENT_CHAT_MAX_MESSAGE_LENGTH.toLocaleString()} characters)`,
				);
				setStatus("error");
				return false;
			}

			const now = Date.now();
			if (now - lastSendAtRef.current < AGENT_CHAT_MIN_SEND_INTERVAL_MS) {
				setError("Please wait a moment before sending another message");
				setStatus("error");
				return false;
			}
			lastSendAtRef.current = now;

			chatLoadRequestIdRef.current += 1;
			abortControllerRef.current?.abort();
			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			isSubmittingRef.current = true;
			setStatus("submitting");
			setError(null);
			continuationMessagesRef.current = null;
			selectionGateRef.current.presentOptionsCalled = false;

			let currentChatId = activeChatIdRef.current;
			if (!currentChatId) {
				try {
					const seedName = chatNameFromMessage(trimmed);
					const createResult = await createChat({
						name: seedName,
					});
					currentChatId = createResult?.chatId ?? null;

					if (currentChatId) {
						seedNameByChatIdRef.current.set(currentChatId, seedName);
						setActiveChatId(currentChatId);
						activeChatIdRef.current = currentChatId;
						setAgentSession({ chatId: currentChatId });
						options?.onChatCreated?.(currentChatId);
					}
				} catch (createErr) {
					console.error(
						"[useAgentChat] Failed to create chat aggregate:",
						createErr,
					);
				}
			}

			const previousMessages = messagesRef.current;
			const baseMessages = options?.baseMessages ?? previousMessages;
			// Sync ref before running the turn so tool gates (e.g. generateImageInChat
			// getPendingChoice) see baseMessages (such as choice-response) without waiting on render.
			messagesRef.current = baseMessages;
			let latestTurnMessages = baseMessages;

			try {
				let peopleContext: string | undefined;
				try {
					const resolvedContext = await resolvePeopleMentionContext({
						text: trimmed,
						mentions: options?.mentions,
						agentTools: activeTools,
					});
					if (resolvedContext) {
						peopleContext = resolvedContext;
					}
				} catch (err) {
					console.warn("[useAgentChat] Failed to resolve mentions:", err);
				}

				let referenceContext: string | undefined;
				try {
					const resolvedRefContext = await resolveReferenceContext({
						text: trimmed,
						references: options?.references,
						agentTools: activeTools,
					});
					if (resolvedRefContext) {
						referenceContext = resolvedRefContext;
					}
				} catch (err) {
					console.warn("[useAgentChat] Failed to resolve references:", err);
				}

				const result = await runAgentChatTurn({
					text: trimmed,
					attachments,
					mentions: options?.mentions,
					peopleContext,
					references: options?.references,
					referenceContext,
					baseMessages,
					llmModel: resolvedLlmModel,
					agentTools: activeTools,
					system: systemPrompt,
					skillAvailability,
					abortSignal: abortController.signal,
					onMessagesChange: (nextMessages) => {
						latestTurnMessages = nextMessages;
						messagesRef.current = nextMessages;
						setMessages(nextMessages);
					},
					onToolExecutionEnd: () => {
						boardAgent?.refreshSnapshot?.();
					},
				});

				applyTurnResult(result);

				if ((result.ok || result.aborted) && currentChatId) {
					const finalMessages =
						result.ok && !result.aborted ? result.messages : latestTurnMessages;
					const messagesToSave = finalMessages.filter(
						(msg, idx) =>
							idx >= previousMessages.length || msg !== previousMessages[idx],
					);
					if (messagesToSave.length > 0) {
						try {
							await saveChatMessages({
								chatId: currentChatId,
								messages: messagesToSave.map((msg) =>
									toSaveChatMessageInput(currentChatId, msg, resolvedLlmModel),
								),
							});
						} catch (saveErr) {
							console.error(
								"[useAgentChat] Failed to save chat messages:",
								saveErr,
							);
						}
					}
					if (result.ok && !result.aborted) {
						tryAutoTitleChat(currentChatId, finalMessages, resolvedLlmModel);
					}
				}
			} catch (caught) {
				const message =
					caught instanceof Error
						? caught.message
						: "Something went wrong. Please try again.";
				setError(message);
				setStatus("error");
			} finally {
				isSubmittingRef.current = false;
				if (abortControllerRef.current === abortController) {
					abortControllerRef.current = null;
				}
			}

			return true;
		},
		[
			agentTools,
			applyTurnResult,
			areToolModelsReady,
			boardAgent,
			ensureMcpToolsLoaded,
			createChat,
			resolvedLlmModel,
			saveChatMessages,
			skillAvailability,
			startNewChat,
			systemPrompt,
			tryAutoTitleChat,
		],
	);

	const continueAfterApprovals = useCallback(
		async (nextMessages: AgentChatMessage[]) => {
			const continuation = continuationMessagesRef.current;
			const approvals = collectApprovalResponses(nextMessages);
			if (!continuation || approvals.length === 0) {
				setError("No pending tool approval to continue");
				setStatus("error");
				return;
			}

			abortControllerRef.current?.abort();
			const abortController = new AbortController();
			abortControllerRef.current = abortController;

			isSubmittingRef.current = true;
			setStatus("submitting");
			setError(null);
			selectionGateRef.current.presentOptionsCalled = false;

			const updatedStartIndex = Math.max(0, nextMessages.length - 1);
			let latestTurnMessages = nextMessages;

			try {
				const activeTools: AgentChatTools = {
					...agentTools,
					...mcpToolsRef.current,
				};

				const result = await continueAgentChatAfterApprovals({
					messages: nextMessages,
					continuationMessages: continuation,
					approvals,
					llmModel: resolvedLlmModel,
					agentTools: activeTools,
					system: systemPrompt,
					skillAvailability,
					abortSignal: abortController.signal,
					onMessagesChange: (updatedMessages) => {
						latestTurnMessages = updatedMessages;
						messagesRef.current = updatedMessages;
						setMessages(updatedMessages);
					},
					onToolExecutionEnd: () => {
						boardAgent?.refreshSnapshot?.();
					},
				});

				applyTurnResult(result);

				const currentChatId = activeChatIdRef.current;
				if ((result.ok || result.aborted) && currentChatId) {
					const finalMessages =
						result.ok && !result.aborted ? result.messages : latestTurnMessages;
					const messagesToSave = finalMessages.slice(updatedStartIndex);
					if (messagesToSave.length > 0) {
						try {
							await saveChatMessages({
								chatId: currentChatId,
								messages: messagesToSave.map((msg) =>
									toSaveChatMessageInput(currentChatId, msg, resolvedLlmModel),
								),
							});
						} catch (saveErr) {
							console.error(
								"[useAgentChat] Failed to save chat messages after approval:",
								saveErr,
							);
						}
					}
					if (result.ok && !result.aborted) {
						tryAutoTitleChat(currentChatId, finalMessages, resolvedLlmModel);
					}
				}
			} catch (caught) {
				const message =
					caught instanceof Error
						? caught.message
						: "Something went wrong. Please try again.";
				setError(message);
				setStatus("error");
			} finally {
				isSubmittingRef.current = false;
				if (abortControllerRef.current === abortController) {
					abortControllerRef.current = null;
				}
			}
		},
		[
			agentTools,
			applyTurnResult,
			boardAgent,
			resolvedLlmModel,
			saveChatMessages,
			skillAvailability,
			systemPrompt,
			tryAutoTitleChat,
		],
	);

	const respondToChoice = useCallback(
		(choiceId: string, optionId: string, _label: string) => {
			if (isSubmittingRef.current) {
				return;
			}

			const validated = validateChoiceSelection(
				messagesRef.current,
				choiceId,
				optionId,
			);
			if (!validated) {
				setError("Invalid option selected");
				setStatus("error");
				return;
			}

			const withResponse = markChoiceResponded(
				messagesRef.current,
				choiceId,
				optionId,
				validated.label,
			);

			setMessages(withResponse);
			void sendMessage(formatChoiceSelectionMessage(validated.label), {
				baseMessages: withResponse,
			});
		},
		[sendMessage],
	);

	const respondToApproval = useCallback(
		(approvalId: string, approved: boolean) => {
			if (isSubmittingRef.current || status !== "awaiting_approval") {
				return;
			}

			const pending = getPendingApprovals(messagesRef.current);
			if (!pending.some((part) => part.approvalId === approvalId)) {
				setError("Invalid approval selection");
				setStatus("error");
				return;
			}

			const withResponse = completeDeniedApprovalToolCalls(
				markApprovalRespondedIncludingDuplicates(
					messagesRef.current,
					approvalId,
					approved,
				),
			);
			messagesRef.current = withResponse;
			setMessages(withResponse);

			if (hasPendingApprovals(withResponse)) {
				return;
			}

			void continueAfterApprovals(withResponse);
		},
		[continueAfterApprovals, status],
	);

	const respondToAllApprovals = useCallback(
		(approved: boolean) => {
			if (isSubmittingRef.current || status !== "awaiting_approval") {
				return;
			}

			const pending = getPendingApprovals(messagesRef.current);
			if (pending.length === 0) {
				return;
			}

			const withResponses = completeDeniedApprovalToolCalls(
				markApprovalsResponded(
					messagesRef.current,
					pending.map((part) => ({
						approvalId: part.approvalId,
						approved,
					})),
				),
			);
			messagesRef.current = withResponses;
			setMessages(withResponses);
			void continueAfterApprovals(withResponses);
		},
		[continueAfterApprovals, status],
	);

	const isAwaitingChoice = status === "awaiting_choice";
	const isAwaitingApproval = status === "awaiting_approval";

	const getActiveChatId = useCallback(() => activeChatIdRef.current, []);
	const getIsTurnInFlight = useCallback(
		() =>
			isSubmittingRef.current ||
			statusRef.current === "submitting" ||
			statusRef.current === "awaiting_choice" ||
			statusRef.current === "awaiting_approval",
		[],
	);

	return useMemo(
		() => ({
			activeChatId,
			getActiveChatId,
			getIsTurnInFlight,
			isLoadingChat,
			messages,
			status,
			error,
			sendMessage,
			startNewChat,
			loadChat,
			abortStream,
			dismissError,
			respondToChoice,
			respondToApproval,
			respondToAllApprovals,
			llmModel,
			setLlmModel,
			toolModels,
			areToolModelsPending,
			toolModelsLoadFailed,
			areToolModelsReady,
			retryToolModelsLoad,
			ensureMcpToolsLoaded,
			isSubmitting: status === "submitting",
			isAwaitingChoice,
			isAwaitingApproval,
		}),
		[
			activeChatId,
			getActiveChatId,
			getIsTurnInFlight,
			isLoadingChat,
			messages,
			status,
			error,
			sendMessage,
			startNewChat,
			loadChat,
			abortStream,
			dismissError,
			respondToChoice,
			respondToApproval,
			respondToAllApprovals,
			llmModel,
			setLlmModel,
			toolModels,
			areToolModelsPending,
			toolModelsLoadFailed,
			areToolModelsReady,
			retryToolModelsLoad,
			ensureMcpToolsLoaded,
			isAwaitingChoice,
			isAwaitingApproval,
		],
	);
}
